/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { createClient, type MatrixClient } from "matrix-js-sdk";
import { backendFetch } from "../utils/backend";
import { initClient } from "../utils/matrix";
import { Config } from "../config/Config";
import type { Session } from "../ClientContext";
import { revokeJoinSession } from "./joinSession";
import type { TFunction } from "i18next";
import { localizedRequestError } from "../utils/localizedError";

export function passkeyErrorMessage(error: unknown, t: TFunction): string {
  if (error instanceof DOMException) {
    if (["NotAllowedError", "AbortError"].includes(error.name))
      return t("instance.ui.passkey_cancelled");
    if (error.name === "InvalidStateError")
      return t("instance.ui.passkey_exists");
    return t("instance.ui.passkey_unavailable");
  }
  return localizedRequestError(error, t);
}

const decode = (value: string): Uint8Array<ArrayBuffer> =>
  Uint8Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/")), (c) =>
    c.charCodeAt(0),
  );
const encode = (value: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(value)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
export async function passkeyRequest(
  path: string,
  body?: unknown,
  client?: MatrixClient,
  signal?: AbortSignal,
): Promise<any> {
  const response = await backendFetch(client, "/api/auth/passkeys" + path, {
    method: body === undefined ? "GET" : "POST",
    credentials: "include",
    signal,
    ...(body === undefined
      ? {}
      : {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
  });
  return response.json();
}
export async function passkeyCeremony(
  options: any,
  registration: boolean,
  signal?: AbortSignal,
): Promise<unknown> {
  const publicKey = { ...options, challenge: decode(options.challenge) };
  if (registration) {
    publicKey.user = { ...options.user, id: decode(options.user.id) };
    publicKey.excludeCredentials = options.excludeCredentials?.map(
      (c: any) => ({ ...c, id: decode(c.id) }),
    );
  } else
    publicKey.allowCredentials = options.allowCredentials?.map((c: any) => ({
      ...c,
      id: decode(c.id),
    }));
  const credential = (await (registration
    ? navigator.credentials.create({ publicKey, signal })
    : navigator.credentials.get({
        publicKey,
        signal,
      }))) as PublicKeyCredential | null;
  if (!credential)
    throw new DOMException("Passkey cancelled", "NotAllowedError");
  const response = credential.response;
  const common = {
    id: credential.id,
    rawId: encode(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
  };
  if (registration) {
    const value = response as AuthenticatorAttestationResponse;
    return {
      ...common,
      response: {
        clientDataJSON: encode(value.clientDataJSON),
        attestationObject: encode(value.attestationObject),
        transports: value.getTransports?.() || [],
      },
    };
  }
  const value = response as AuthenticatorAssertionResponse;
  return {
    ...common,
    response: {
      clientDataJSON: encode(value.clientDataJSON),
      authenticatorData: encode(value.authenticatorData),
      signature: encode(value.signature),
      userHandle: value.userHandle ? encode(value.userHandle) : null,
    },
  };
}
export async function loginWithPasskey(
  oldClient?: MatrixClient,
): Promise<[MatrixClient, Session]> {
  const { flow, options } = await passkeyRequest("/login/options", {});
  const response = await passkeyCeremony(options, false);
  const { session, joinSession } = (await passkeyRequest("/login/verify", {
    flow,
    response,
  })) as { session: Session; joinSession: Session };
  const baseUrl = Config.defaultHomeserverUrl()!;
  const call = createClient({
    baseUrl,
    accessToken: session.access_token,
    userId: session.user_id,
    deviceId: session.device_id,
  });
  const join = createClient({
    baseUrl,
    accessToken: joinSession.access_token,
    userId: joinSession.user_id,
    deviceId: joinSession.device_id,
  });
  try {
    if (
      session.homeserver_url?.replace(/\/$/, "") !==
        baseUrl.replace(/\/$/, "") ||
      joinSession.homeserver_url?.replace(/\/$/, "") !==
        baseUrl.replace(/\/$/, "") ||
      joinSession.user_id !== session.user_id ||
      joinSession.device_id === session.device_id
    )
      throw Error("Passkey session mismatch");
    await backendFetch(call, "/api/auth/join-session/prepare", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_token: joinSession.access_token,
        device_id: joinSession.device_id,
      }),
    });
    if (oldClient) {
      await revokeJoinSession(oldClient);
      await oldClient.logout(true);
    }
    const client = await initClient(
      {
        baseUrl,
        accessToken: session.access_token,
        userId: session.user_id,
        deviceId: session.device_id,
      },
      false,
    );
    return [client, { ...session, passwordlessUser: false }];
  } catch (error) {
    await Promise.all([
      call.logout().catch(() => {}),
      join.logout().catch(() => {}),
    ]);
    throw error;
  }
}
